/**
 * 見積依頼（#119、docs/56）の画面の小さな部品：状態のしるし・見積の行のしるし（仕入元の列）・日付。
 *   期限超過は朱の文字（色だけで伝えない＝「期限超過 n日」の文字を添える）
 */
import { RFQ_MSG, RFQ_STATUS_LABEL, type RfqLineMark, type RfqSummary } from "@genbaryoku/contract";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";

/** 「10/13」 */
export const md = (d: string | null | undefined): string => {
  if (!d) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${Number(m[2])}/${Number(m[3])}` : d;
};
/** 「10月13日」 */
export const mdLong = (d: string | null | undefined): string => {
  if (!d) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${Number(m[2])}月${Number(m[3])}日` : d;
};
/** ISO の日時 → 「10/6 14:05」（日本時間） */
export const mdTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};

/** 朱の日数の文（#218）：回答期限があれば「期限超過 n日」、空なら「送付から n日」（送付から 14 日で朱） */
export const overdueText = (s: Pick<RfqSummary, "overdueDays" | "replyBy">): string =>
  s.overdueDays ? (s.replyBy ? RFQ_MSG.overdue : RFQ_MSG.sinceSent).replace("{n}", String(s.overdueDays)) : "";

/** 一覧の「明細」の列（#218、Figma ①）：上の段「n行」・下の段「見積 n・追加 n」か「添付のみ」 */
export function linesCell(s: Pick<RfqSummary, "lineCounts" | "itemCount">): { top: string; sub: string } {
  const { estimate, own } = s.lineCounts;
  const n = estimate + own;
  const sub = n === 0 ? RFQ_MSG.attachOnly : [estimate ? RFQ_MSG.lineSplitEstimate.replace("{n}", String(estimate)) : "", own ? RFQ_MSG.lineSplitOwn.replace("{n}", String(own)) : ""].filter(Boolean).join("・");
  return { top: RFQ_MSG.lineCount.replace("{n}", String(n)), sub };
}
/** 一覧の「添付」の列（#218、Figma ①）：「図面 2・写真 1」。無ければ — */
export function attachCell(s: Pick<RfqSummary, "attachCounts">): string {
  const { drawing, photo, other } = s.attachCounts;
  return [drawing ? RFQ_MSG.attachDrawing.replace("{n}", String(drawing)) : "", photo ? RFQ_MSG.attachPhoto.replace("{n}", String(photo)) : "", other ? RFQ_MSG.attachOther.replace("{n}", String(other)) : ""].filter(Boolean).join("・") || "—";
}
/** 回答の金額（{vendorQuote}の税込の合計）。無ければ空 */
export const answerAmount = (s: Pick<RfqSummary, "answer">): string => (s.answer?.total != null ? yen(s.answer.total) : "");

/** 状態のしるし（一覧・帯・依頼書の帯）。差し替え済・期限超過は文字を添える */
export function RfqStatus({ s, compact }: { s: Pick<RfqSummary, "status" | "overdueDays" | "supersededBy" | "replyBy">; compact?: boolean }) {
  return (
    <span className="rqstate" data-testid="rfq-status">
      <span className={"chip" + (s.status === "draft" || s.status === "cancelled" ? " quiet" : "")} data-status={s.status}>
        {RFQ_STATUS_LABEL[s.status]}
      </span>
      {s.supersededBy ? <span className="muted">{RFQ_MSG.supersededBy.replace("{no}", s.supersededBy)}</span> : null}
      {s.status === "sent" && !s.supersededBy && s.overdueDays ? (
        <span className="shu" data-testid="rfq-overdue">{overdueText(s)}</span>
      ) : s.status === "sent" && !s.supersededBy && s.replyBy && !compact ? (
        <span className="muted">{`${RFQ_MSG.markReply} ${md(s.replyBy)}`}</span>
      ) : null}
    </span>
  );
}

/**
 * 見積の行のしるし（#218、docs/88 §4.5）：1行に何通でも。回答のある社を先に「回答あり A社」、次に「依頼済 B社・C社」、3社目からは「ほか n社」。
 * 乗せると社ごとに番号・回答期限。names＝{vendor}の id → 略称
 */
export function rfqMarksView(marks: RfqLineMark[], names: (vendorId: string) => string): { text: string; head: string; tail: string; title: string; overdue: boolean; state: "sent" | "answered" } {
  const byNo = (a: RfqLineMark, b: RfqLineMark) => (a.no ?? "").localeCompare(b.no ?? "", "ja", { numeric: true });
  const answered = marks.filter((m) => m.state === "answered").sort(byNo);
  const sent = marks.filter((m) => m.state === "sent").sort(byNo);
  const order = [...answered, ...sent];
  const shown = order.slice(0, 2).map((m) => names(m.vendorId));
  const more = order.length > 2 ? RFQ_MSG.markMore.replace("{n}", String(order.length - 2)) : "";
  const head = answered.length ? RFQ_MSG.markAnswered : RFQ_MSG.markSent;
  const title = order
    .map((m) => `${names(m.vendorId)}　${m.no ?? ""}　${m.state === "answered" ? RFQ_MSG.markAnswered : m.replyBy ? `回答期限 ${mdLong(m.replyBy)}` : ""}${m.overdueDays ? `（${m.replyBy ? RFQ_MSG.overdue : RFQ_MSG.sinceSent}）`.replace("{n}", String(m.overdueDays)) : ""}`.trim())
    .join("\n");
  const tail = [shown.join("・"), more].filter(Boolean).join(" ");
  return { text: `${head} ${tail}`, head, tail, title, overdue: sent.some((m) => m.overdueDays) && !answered.length, state: answered.length ? "answered" : "sent" };
}

/** 見積の行のしるし（docs/56 §6.1）。short＝外注先の略称、full＝正式名 */
export function rfqMarkView(mark: RfqLineMark | undefined, short: string, full: string): { text: string; head: string; tail: string; title: string; overdue: boolean; state: "unsent" | "sent" | "answered" } {
  if (!mark || mark.state === "unsent") {
    return { text: `${RFQ_MSG.markUnsent} ${short}`, head: RFQ_MSG.markUnsent, tail: "", title: t(`${full}　見積依頼書を送っていません`), overdue: false, state: "unsent" };
  }
  if (mark.state === "answered") {
    return { text: `${RFQ_MSG.markAnswered} ${short}`, head: RFQ_MSG.markAnswered, tail: "", title: `${full}　${mark.no ?? ""}　回答 ${mark.answeredAt ? mdLong(mark.answeredAt.slice(0, 10)) : ""}`.trim(), overdue: false, state: "answered" };
  }
  if (mark.overdueDays) {
    return { text: `${RFQ_MSG.markSent} ${short} ${RFQ_MSG.markOverdue}`, head: RFQ_MSG.markSent, tail: RFQ_MSG.markOverdue, title: `${full}　${mark.no ?? ""}　回答期限 ${mdLong(mark.replyBy)}（${mark.overdueDays}日過ぎています）`, overdue: true, state: "sent" };
  }
  return { text: `${RFQ_MSG.markSent} ${short} ${RFQ_MSG.markReply} ${md(mark.replyBy)}`, head: RFQ_MSG.markSent, tail: mark.replyBy ? `${RFQ_MSG.markReply} ${md(mark.replyBy)}` : "", title: `${full}　${mark.no ?? ""}　回答期限 ${mdLong(mark.replyBy)}`, overdue: false, state: "sent" };
}
